/**
 * SUB-5.1 · FR44. The portfolio roll-up, as arithmetic.
 *
 * Everything on the All Accounts dashboard that is a decision rather than a
 * render lives here, so it can be tested without a DOM. `PortfolioRollup.tsx`
 * lays this out and adds nothing to it.
 *
 * ⚠️ THIS DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING. Same standing rule as
 * `mergedFilters.ts`, `mergedMetrics.ts` and `accountAttribution.ts`: the
 * accounts the page may read across is `merged_scope_tenant_ids()` evaluated
 * inside `merged_scope_project_sla()`, and nothing here may ever be why a row is
 * absent.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR DECISIONS THIS FILE IMPLEMENTS, chosen 2026-08-15 from rendered
 * mockups the way D21 closed UX-GAP-1, 2 and 3. Options and the rejected
 * alternatives: `https://claude.ai/code/artifact/f4cd744a-1a03-4981-8ad8-4808910eb403`
 *
 * | | Decision | |
 * |---|---|---|
 * | **D47** | **A2.** The page leads with accounts RANKED by how badly they are overrunning, and demotes the absolute counts to supporting figures. | §1 |
 * | **D48** | **B2.** M1 reports coverage as its own figure, so "late" and "never measured" are two numbers rather than one. | §2 |
 * | **D49** | **C1.** FR46's refusal is a named section reusing the filter panel's wording, generated from `mergedMetrics.ts`. | in the component |
 * | **D50** | **D1.** M3 names the caller's own team and groups each client's staff by account. Recorded here, built by SUB-5.3. | not in this file |
 *
 * ---------------------------------------------------------------------------
 * §1 · WHY A RANKING AT ALL, AND WHY THIS ONE
 *
 * Measured live 2026-08-15: **93%** of the projects that have an SLA configured
 * are past it, and **90%** of the whole portfolio has not changed stage in 30
 * days. A page whose headline is "312 past SLA" out of 340 tells Dana to look at
 * everything, which is what she does today and is the problem FR44 exists to
 * solve. Absolute counts do not discriminate on this data. Relative ones do.
 *
 * ⚠️ **THE RANK IS LEXICOGRAPHIC, NOT A WEIGHTED SCORE, AND THAT IS DELIBERATE.**
 * Worst overrun first, then oldest untouched, then name. A weighted composite
 * would rank accounts by a judgement about how much a day of overrun is worth
 * against a day of silence, and nobody has made that judgement: it is not in the
 * PRD, it is not one of FR48's four metrics, and inventing one here would put an
 * unreviewable constant at the top of the dashboard. Two explainable sorts beat
 * one unexplainable number, and a reader can always see why a row is where it is.
 *
 * §2 · WHY COVERAGE IS ITS OWN FIGURE
 *
 * **67%** of projects sit in a stage with no SLA configured, where
 * `sla_breach_days` returns NULL and the project is invisible to M1. Reporting
 * only "312 past SLA" hides that; reporting "312 of 334 measured" alongside "19%
 * of accounts set no SLA" separates a delivery problem from a configuration one.
 * The second is fixable by a conversation, which is a better thing for a
 * dashboard to produce than alarm.
 *
 * ⚠️ **THREE STATES, NEVER TWO.** `is_past_sla` is NULL when `stage_changed_at`
 * is NULL: the clock is unreadable, which is not the same as "not late".
 * SUB-5.2's function returns that third state on purpose and this file carries it
 * through to `unknown` rather than folding it into a pass. Zero projects are in
 * that state today and the column is nullable, so it is handled rather than
 * assumed away. Folding it in would be the silent under-count NFR8 forbids.
 */

import { accountLabelFor } from './accountAttribution';
import { withheldInMergedScope, type MergedMetricRule } from './mergedMetrics';

/**
 * One row of `public.merged_scope_project_sla()`, shipped by SUB-5.2's
 * `20260815a`. The names are the function's output columns.
 */
export interface ProjectSlaRow {
  project_id: string;
  tenant_id: string;
  stage_id: number | null;
  sla_enabled: boolean;
  /** Days after which this project is late, or null when no SLA is configured. */
  breach_days: number | null;
  days_in_stage: number | null;
  /** true late, false on time, ⚠️ null means the clock could not be read. */
  is_past_sla: boolean | null;
}

export interface AccountRollup {
  tenantId: string;
  /** The account's own name, or a stable fallback. Never a bare uuid on screen. */
  name: string;
  /**
   * Whether `name` is real or a stand-in built from the id.
   *
   * ⚠️ False is not an error to hide. An EMPTY account cell reads as "mine",
   * which is the mis-attribution FR34 exists to prevent, so the page renders the
   * stand-in and may mark it.
   */
  nameKnown: boolean;
  /** Every project the caller can see in this account. */
  total: number;
  /** Those with an SLA configured. M1's denominator. */
  measured: number;
  /** Of the measured, those past it. */
  pastSla: number;
  /** ⚠️ Measured, but the clock could not be read. Neither late nor on time. */
  unknown: number;
  /** Days past the threshold for the worst offender, or null if none is late. */
  worstOverrunDays: number | null;
  /** Longest time any project here has sat in its current stage. Rounded UP. */
  oldestUntouchedDays: number | null;
  /**
   * The same quantity rounded DOWN, and it exists only to be a filter threshold.
   *
   * ⚠️ **THE DISPLAYED FIGURE CANNOT BE ITS OWN FILTER.** `oldestUntouchedDays`
   * is `ceil(max age)`, and the drill-down turns a threshold into
   * `.lte('stage_changed_at', now - N days)`, which is the continuous test
   * "elapsed >= N". `days_in_stage` comes from `EXTRACT(epoch …)/86400.0` and is
   * never a whole number, so the oldest project sits at `N - f` for some
   * `f` in (0,1) and was excluded by the very link that named it. Being the
   * maximum, everything else was excluded too: the cell opened an empty list.
   *
   * Rounding down gives a threshold the subject satisfies. The list is then at
   * worst a superset by under a day, which is the direction NFR8 permits.
   */
  oldestUntouchedFilterDays: number | null;
  /** True when this is the caller's own account rather than a client's. */
  isAnchor: boolean;
}

export interface PortfolioCoverage {
  accounts: number;
  projects: number;
  /** Projects with an SLA configured. */
  measured: number;
  /** Projects in a stage nobody set an SLA on. M1 cannot see these at all. */
  unmeasured: number;
  /** Accounts that configured no SLA anywhere. */
  accountsWithoutSla: number;
  pastSla: number;
  unknown: number;
}

/**
 * Days past the breach threshold, or null when the project is not late or
 * cannot be judged.
 *
 * ⚠️ `Math.ceil` on the elapsed days, matching `getSlaColor`, so a figure on
 * this page never disagrees with the colour the pipeline is painting for the
 * same project. D45's note explains why matching the display beat the purer
 * continuous test.
 *
 * ⚠️ **AND `Math.ceil` ON THE RESULT, BECAUSE THE THRESHOLD IS NOT AN INTEGER.**
 * `sla_breach_days`'s object branch returns `hours / 24.0`
 * (`20260815a:189-196`), so `breach_days` can be `0.5`. Subtracting it from a
 * whole number left a fraction, and the cell renders `+{value}d` inside a
 * `tabular-nums` column of integers: "+11.5d". Rounding UP keeps the one
 * convention this file states, that elapsed time is never rounded down toward
 * looking less late.
 *
 * ⚠️ Zero is a real answer, not a bug: `>=` admits the project whose elapsed
 * time has just reached the threshold, and "+0d" says exactly that. It is the
 * boundary, not a rounding artefact.
 */
/**
 * SUB-5.10 · AC6. Why an agency's totals will not match a client's own.
 *
 * ⚠️ THIS IS NOT A DISCLAIMER, IT IS THE RULE THE NUMBERS ARE COMPUTED BY.
 * `sla_breach_days` is read per account and D45 takes THAT account's maximum, so
 * two accounts asking "is this past SLA" are asking different questions and
 * getting correct different answers. Adding their answers together is meaningful
 * as a count of projects needing attention, and meaningless as a shared
 * threshold.
 *
 * It is said on the surface because support will otherwise receive it as a bug
 * the first time an agency and a client compare figures, and because a number
 * whose rule is invisible is a number nobody can check.
 */
export const PER_ACCOUNT_THRESHOLD_NOTE =
  'Each account sets its own SLA, so these counts use every account\'s own thresholds rather than one shared rule.';

export function overrunDays(row: ProjectSlaRow): number | null {
  if (row.is_past_sla !== true) return null;
  if (row.breach_days === null || row.days_in_stage === null) return null;
  return Math.ceil(Math.ceil(row.days_in_stage) - row.breach_days);
}

/**
 * Fold the per-project rows into one entry per account.
 *
 * ⚠️ `names` is the Map `buildAccountNames()` returns, and the labelling goes
 * through `accountLabelFor()` rather than being reimplemented here. That helper
 * already owns the contract this page depends on: never empty, and it says
 * whether the name is real or a stand-in. A second spelling of it would drift.
 */
export function rollUpByAccount(
  rows: readonly ProjectSlaRow[],
  names: ReadonlyMap<string, string>,
  anchorTenantId: string | null,
): AccountRollup[] {
  const byTenant = new Map<string, AccountRollup>();

  for (const row of rows) {
    let acc = byTenant.get(row.tenant_id);
    if (!acc) {
      const label = accountLabelFor(row.tenant_id, names);
      acc = {
        tenantId: row.tenant_id,
        name: label.name,
        nameKnown: label.known,
        total: 0,
        measured: 0,
        pastSla: 0,
        unknown: 0,
        worstOverrunDays: null,
        oldestUntouchedDays: null,
        oldestUntouchedFilterDays: null,
        isAnchor: row.tenant_id === anchorTenantId,
      };
      byTenant.set(row.tenant_id, acc);
    }

    acc.total += 1;

    if (row.days_in_stage !== null) {
      const age = Math.ceil(row.days_in_stage);
      if (acc.oldestUntouchedDays === null || age > acc.oldestUntouchedDays) {
        acc.oldestUntouchedDays = age;
        // ⚠️ Taken from the SAME row, not derived from the ceiling afterwards.
        // `ceil(x) - 1` is wrong whenever x is a whole number, and floor of the
        // raw value is right in every case. See `oldestUntouchedFilterDays`.
        acc.oldestUntouchedFilterDays = Math.floor(row.days_in_stage);
      }
    }

    // No SLA configured: invisible to M1, and counted only in `total`. This is
    // the 67% the coverage figure exists to name.
    if (row.breach_days === null) continue;

    acc.measured += 1;
    if (row.is_past_sla === null) {
      acc.unknown += 1;
      continue;
    }
    if (row.is_past_sla) {
      acc.pastSla += 1;
      const over = overrunDays(row);
      if (over !== null && (acc.worstOverrunDays === null || over > acc.worstOverrunDays)) {
        acc.worstOverrunDays = over;
      }
    }
  }

  return [...byTenant.values()];
}

/**
 * D47's order: worst overrun first, then oldest untouched, then name.
 *
 * ⚠️ An account with nothing late sorts BELOW every account with something late,
 * however old its work is. That is the intended reading: "past a threshold this
 * account itself set" is a stronger signal than elapsed time, which is exactly
 * why M1 and M2 are a pair in FR48 rather than one metric.
 */
export function rankAccounts(accounts: readonly AccountRollup[]): AccountRollup[] {
  return [...accounts].sort((a, b) => {
    const ao = a.worstOverrunDays;
    const bo = b.worstOverrunDays;
    if (ao !== bo) {
      if (ao === null) return 1;
      if (bo === null) return -1;
      return bo - ao;
    }
    const au = a.oldestUntouchedDays ?? -1;
    const bu = b.oldestUntouchedDays ?? -1;
    if (au !== bu) return bu - au;
    return a.name.localeCompare(b.name);
  });
}

/**
 * D48's split. `accountsWithoutSla` counts accounts where nothing is measured at
 * all, which is the figure that turns a silent gap into a conversation.
 */
export function portfolioCoverage(accounts: readonly AccountRollup[]): PortfolioCoverage {
  const totals = accounts.reduce(
    (acc, a) => {
      acc.projects += a.total;
      acc.measured += a.measured;
      acc.pastSla += a.pastSla;
      acc.unknown += a.unknown;
      if (a.measured === 0) acc.accountsWithoutSla += 1;
      return acc;
    },
    { projects: 0, measured: 0, pastSla: 0, unknown: 0, accountsWithoutSla: 0 },
  );

  return {
    accounts: accounts.length,
    projects: totals.projects,
    measured: totals.measured,
    unmeasured: totals.projects - totals.measured,
    accountsWithoutSla: totals.accountsWithoutSla,
    pastSla: totals.pastSla,
    unknown: totals.unknown,
  };
}

/**
 * The metrics the roll-up refuses to aggregate, for D49's named section.
 *
 * ⚠️ Read from `mergedMetrics.ts` rather than listed here. A second list would
 * drift from the registry, and the registry is what actually decides: a metric
 * added there as account-scoped has to appear in this section without anybody
 * remembering to add it.
 */
export function withheldMetrics(): MergedMetricRule[] {
  return withheldInMergedScope();
}

/**
 * ⚠️ FR44 asks for a surface "built for cross-account aggregation", and an empty
 * one is a legitimate answer rather than an error. A parent whose sub-accounts
 * hold no projects yet gets a page that says so; it is not a failure and must not
 * render as one. NFR6: losing the merged view must never look like losing access.
 */
export function isEmptyPortfolio(coverage: PortfolioCoverage): boolean {
  return coverage.projects === 0;
}
